import i18n from "i18next";
import { Trans } from "react-i18next";
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { usePlan } from "@/hooks/usePlan";
import { IconX, IconGift } from "@tabler/icons-react";

const STORAGE_KEY = "lilia.beta-banner.dismissed-until";

/**
 * Thin top-of-page banner shown during the beta period. Renders only
 * on non-document layout views (excluded from /d/* via the
 * outer Layouts BLANK_LAYOUT_PATHS guard, so the editor surface
 * itself stays uncluttered).
 *
 * Dismissable for 24h via localStorage — the message resurfaces the
 * next day so users keep the "this is beta, paid is coming" frame
 * top-of-mind without becoming wallpaper.
 *
 * While shown it publishes its height as `--banner-h` on the root, so the
 * full-height tool pages (`calc(100dvh - var(--bar-h) - var(--banner-h))`)
 * end at the window's edge instead of pushing their rail's foot — Trash —
 * below it.
 */
export function BetaBanner() {
  const [hidden, setHidden] = useState(true); // hidden until SSR-safe check completes
  // "Full features unlocked" stops being true the day plan limits apply.
  const { data: plan } = usePlan();
  const shown = !hidden && !plan?.enforced;
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    if (!shown || !el) {
      root.style.setProperty("--banner-h", "0px");
      return;
    }
    const publish = () => root.style.setProperty("--banner-h", `${el.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.setProperty("--banner-h", "0px");
    };
  }, [shown]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const dismissedUntil = window.localStorage.getItem(STORAGE_KEY);
    if (dismissedUntil) {
      const ts = Number(dismissedUntil);
      if (Number.isFinite(ts) && ts > Date.now()) {
        return; // still within the dismiss window
      }
    }
    setHidden(false);
  }, []);

  if (!shown) return null;

  const handleDismiss = () => {
    if (typeof window !== "undefined") {
      const oneDay = 24 * 60 * 60 * 1000;
      window.localStorage.setItem(STORAGE_KEY, String(Date.now() + oneDay));
    }
    setHidden(true);
  };

  return (
    <div
      ref={ref}
      data-testid="beta-banner"
      role="status"
      aria-live="polite"
      className="flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium border-b border-[var(--lilia-border,#e2e8f0)]"
      style={{
        background:
          "linear-gradient(90deg, var(--ip-accent-wash, #efe9ff) 0%, var(--ip-paper, #fffefb) 50%, var(--ip-accent-wash, #efe9ff) 100%)",
        color: "var(--ip-accent-deep, #2d1f5e)",
      }}
    >
      <IconGift size={14} className="shrink-0" />
      <span>
        <Trans i18nKey="shell:beta.text" components={{ strong: <strong /> }} />
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={i18n.t("shell:beta.dismiss")}
        data-testid="beta-banner-dismiss"
        className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded hover:bg-[var(--ip-accent-mild,#d4c8f5)]/30 transition-colors"
      >
        <IconX size={12} />
      </button>
    </div>
  );
}
